export type DemoStatus = 'interactive' | 'planned'

export type DemoDomain = {
  /** Stable identifier, also used as the Demo Center anchor (#government). */
  id: 'government' | 'research' | 'education' | 'talent' | 'community' | 'leadership' | 'clinical'
  title: string
  solutionLabel: string
  description: string
  workspaces: string[]
  status: DemoStatus
  actionLabel: string
  /** Only set when a working demonstration exists. */
  demoUrl?: string
  detail: string
  workflow: string[]
  note?: string
  /** Demo notice shown for working demos. */
  demoNotice?: string
  /** Existing site page for this domain, when one exists. */
  relatedRoute?: { href: string; label: string }
}

export const demoCenterPath = '/noetivra/demo-center'

export const governmentDemoUrl = 'https://noetivra-workspace.abledream3.chatgpt.site/#chooser'

export const governmentDemoNotice = 'Fictional records · Browser-local saving · Demonstration workspace access'

export const demoStatusLabels: Record<DemoStatus, { label: string; meaning: string }> = {
  interactive: { label: 'Interactive demo', meaning: 'A working demonstration is available.' },
  planned: {
    label: 'Planned demo',
    meaning: 'The intended experience is described, but an interactive demonstration is not yet available.',
  },
}

export const demoDomains: DemoDomain[] = [
  {
    id: 'government',
    title: 'Government & Public Institutions',
    solutionLabel: 'National Capability and Development System',
    description:
      'Connect citizen development and practical support with program delivery, geographic oversight and accountable institutional decisions.',
    workspaces: ['Citizen Workspace', 'Administration Control Panel'],
    status: 'interactive',
    actionLabel: 'Open Government Demo',
    demoUrl: governmentDemoUrl,
    detail:
      'Explore the fictional Republic of Nordland. Follow a citizen’s development and support journey, review referrals and appeals, and drill from national reporting through regions, states, provinces and cities to districts.',
    workflow: [],
    demoNotice: governmentDemoNotice,
    relatedRoute: { href: '/government', label: 'Government solution' },
  },
  {
    id: 'research',
    title: 'Scientific Research',
    solutionLabel: 'Noetivra Research',
    description:
      'Connect concepts, studies, methods and findings while keeping supporting passages and review decisions visible.',
    workspaces: ['Research Workspace', 'Curation & Administration'],
    status: 'planned',
    actionLabel: 'Explore planned workflow',
    detail:
      'Follow a research question from source documents to a reviewed knowledge graph. Inspect proposed relationships, compare conflicting findings and trace approved knowledge to its sources.',
    workflow: [
      'Explore sources',
      'Inspect proposed relationships',
      'Review evidence',
      'Approve knowledge',
      'Compare findings',
    ],
    relatedRoute: { href: '/noetivra#research', label: 'Noetivra Research' },
  },
  {
    id: 'education',
    title: 'Education & Learning',
    solutionLabel: 'Noetivra for Education',
    description:
      'Connect learning activities, evidence-based feedback and development plans to help learners build reasoning and self-direction over time.',
    workspaces: ['Learner Workspace', 'Educator & Institution Workspace'],
    status: 'planned',
    actionLabel: 'Explore planned workflow',
    detail:
      'A learner evaluates conflicting claims, reviews feedback with an educator and completes a focused development activity. The institution examines participation, support needs and progress.',
    workflow: ['Complete activity', 'Review findings', 'Agree development', 'Practice', 'Reassess'],
    relatedRoute: { href: '/education', label: 'Education solution' },
  },
  {
    id: 'talent',
    title: 'Employers & Workforce',
    solutionLabel: 'Noetivra Talent',
    description:
      'Connect role requirements with demonstrated skills, structured review and practical development opportunities.',
    workspaces: ['Candidate & Employee Workspace', 'Reviewer & Employer Workspace'],
    status: 'planned',
    actionLabel: 'Explore planned workflow',
    detail:
      'Explore a role requirements view, inspect candidate work samples and compare evidence using consistent criteria. Reviewers document disagreements, follow-up questions and development priorities.',
    workflow: [
      'Define requirements',
      'Collect evidence',
      'Review findings',
      'Document a human decision',
      'Support development',
    ],
    note: 'Development guidance and consequential hiring decisions remain distinct. Assessment methods require evidence appropriate to their intended use.',
    relatedRoute: { href: '/employers', label: 'Employers solution' },
  },
  {
    id: 'community',
    title: 'Civil Society & Community',
    solutionLabel: 'Noetivra for Communities',
    description:
      'Connect community knowledge, mentorship and support programs while preserving each organization’s control over its records.',
    workspaces: ['Participant Workspace', 'Mentor & Program Workspace'],
    status: 'planned',
    actionLabel: 'Explore planned workflow',
    detail:
      'Follow a participant through a mentoring pathway, explore community resources and coordinate an authorized referral between organizations.',
    workflow: ['Identify a goal', 'Connect support', 'Deliver activities', 'Review progress', 'Agree next steps'],
    note: 'Community organizations can participate in shared programs without receiving unrestricted access to government or partner records.',
    relatedRoute: { href: '/civil-society', label: 'Civil Society solution' },
  },
  {
    id: 'leadership',
    title: 'Leadership Development',
    solutionLabel: 'Noetivra Leadership',
    description: 'Connect consequential decision exercises with evidence review, coaching and development over time.',
    workspaces: ['Participant Workspace', 'Coach & Program Workspace'],
    status: 'planned',
    actionLabel: 'Explore planned workflow',
    detail:
      'A participant works through a demanding scenario, explains their reasoning, reviews feedback and revisits the decision after targeted development.',
    workflow: [
      'Explore a scenario',
      'Explain a decision',
      'Review reasoning',
      'Practice with support',
      'Reassess',
    ],
    note: 'Leadership is a shared module that can be configured for government, employers, education and community programs.',
    relatedRoute: { href: '/leadership', label: 'Leadership solution' },
  },
  {
    id: 'clinical',
    title: 'Clinical Review',
    solutionLabel: 'Noetivra Clinical',
    description:
      'Bring patient accounts, clinical history, structured assessments and professional observations together with supporting clinical knowledge.',
    workspaces: ['Clinician Workspace', 'Authorized Review & Practice Administration'],
    status: 'planned',
    actionLabel: 'Explore planned workflow',
    detail:
      'Explore a fictional clinical timeline that distinguishes patient reports, recorded observations and professional interpretations. Inspect relevant references and compare information across visits.',
    workflow: [
      'Gather history',
      'Organize observations',
      'Inspect references',
      'Review alternatives',
      'Document clinical assessment',
    ],
    note: 'Clinical review does not require civic judgment assessment. Clinicians retain responsibility for assessment and diagnosis.',
    relatedRoute: { href: '/noetivra#clinical', label: 'Noetivra Clinical' },
  },
]

export const demoDomainHref = (id: DemoDomain['id']) => `${demoCenterPath}#${id}`

export const getDemoDomain = (id: DemoDomain['id']) => demoDomains.find((domain) => domain.id === id)

export const governmentWorkspacePreviews = [
  { title: 'Citizen Workspace', body: 'Personal development, services, requests and review.' },
  { title: 'Administration Control Panel', body: 'Citizen records, delivery workflows and geographic oversight.' },
]

export const foundationCards = [
  {
    name: 'SKP — Connected knowledge',
    body: 'Explore sources, concepts, relationships and approved versions with their provenance.',
    href: '/semantic-knowledge',
  },
  {
    name: 'HCF — Assessment and development',
    body: 'Connect structured tasks, review, development activities and reassessment where capability development is part of the solution.',
    href: '/human-capability',
  },
  {
    name: 'Noetivra — Interactive workspaces',
    body: 'Bring evidence, workflows and decisions into clear interfaces for participants, professionals and administrators.',
    href: '/noetivra',
  },
]
